import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fromLonLat } from "ol/proj";
import GeoJSON from "ol/format/GeoJSON";
import { getCenter, extend, createEmpty } from "ol/extent";
import { RMap, RLayerTile, RLayerVector, RLayerCluster, RFeature } from "rlayers";
import { RStyle, RFill, RIcon, RStroke, RText } from "rlayers/style";

import "ol/ol.css";
import { Radio } from "../../../../../vendor";
import useHeatMapRoute from "../../../helpers/useHeatMapRoute";
import useHeatMapLocation from "../../../helpers/useHeatMapLocation";
import { dataToGeoJSON, routeToGeom, longLatToGeo } from "../../../helpers/dataToGeoJSON";
import { Map } from "ol";

// Default viewport properties - the center is DE
const defaultCenter = { center: fromLonLat([9, 51]), zoom: 6 };

const FeatureReader = new GeoJSON({ featureProjection: "EPSG:3857" });

const fitOptions = {};

// Calculate bounding box by combining extent of each feature
const generateBoundingBox = (data) =>
  data.map((f) => f.getGeometry().getExtent()).reduce((a, b) => extend(a, b), createEmpty());

export default (props) => {
  /**
   * A workaround to acquire OpenLayer map object
   * @type {React.MutableRefObject<Map>}
   */
  const thisMap = useRef();
  const zoomingOnFilter = useRef();
  const [routeData, fetchRouteData] = useHeatMapRoute();
  const [locationData, fetchlocationData] = useHeatMapLocation();
  // Acquire API data
  // Currently, bike(location) and routes data are came from useHeatMapRoute
  // There exists useHeatMapPosition for future use of API /getPositions
  const routesJSON = useMemo(() => FeatureReader.readFeatures(routeData["routes"]), [routeData]);
  const bikeJSON = useMemo(() => FeatureReader.readFeatures(routeData["endpoints"]), [routeData]);
  const locationJSON = useMemo(() => FeatureReader.readFeatures(locationData), [locationData]);
  // Fetch data from new filter
  useEffect(() => {
    fetchRouteData(props.filter);
  }, [props.filter, fetchRouteData]);

  useEffect(() => {}, [routesJSON]);

  useEffect(() => {
    fetchlocationData();
  }, [fetchlocationData]);
  // Accessing inner view handle of RMap
  const [view, setView] = React.useState(defaultCenter);
  // Update new bounding box
  useEffect(() => {
    if (locationJSON.length > 0) {
      const box = generateBoundingBox(locationJSON);
      if (thisMap.current) {
        const m = thisMap.current;
        // Add slight delay so view can change
        clearTimeout(zoomingOnFilter.current);
        m.getView().fit(box, fitOptions);
        zoomingOnFilter.current = setTimeout(() => {
          m.getView().fit(box, fitOptions);
          const currentZoom = m.getView().getZoom();

          if (currentZoom < 11.42268681370771) {
            m.getView().setZoom(m.getView().getZoom() - 0.5);
          } else {
            m.getView().setZoom(11.42268681370771 - 0.6);
          }
        }, 200);
        //zoomingOnFilter.current = m.getView().fit(box, fitOptions);
      } else {
        // backup method in case map was not acquired in time
        //setView({ center: getCenter(box), zoom: defaultCenter.zoom });
      }
    }
  }, [locationJSON]);

  // Loading indicator: visibility cycle
  useEffect(() => {
    const app = Radio.channel("app");
    app.trigger("collection:pagination:end");
  }, [routeData]);
  useEffect(() => {
    const app = Radio.channel("app");
    app.trigger("collection:pagination:start");
    return () => {
      app.trigger("collection:pagination:end");
    };
  }, [props.filter]);

  // Force re-mount the cluster layer so data can be updated
  // ... since RLayerCluster cannot be dynamically updated
  // ... using JSX `RFeature`s
  const [isClusterOld, setClusterOld] = useState(false);
  useEffect(() => {
    if (isClusterOld) setClusterOld(false);
  }, [isClusterOld]);
  useEffect(() => {
    setClusterOld(true);
  }, [bikeJSON]);

  // This is the hashing function for cluster layer,
  // Currently, it's based on id of the OpenLayer object
  const clusterCache = useCallback((feature, resolution) => "#" + feature.ol_uid, []);

  const BikeIcon = useMemo(
    () => (
      <RIcon
        src="https://inbooma.s3.eu-central-1.amazonaws.com/images/location_marker_icon_49.svg"
        // Center-above the point (top-left is [0, 0])
        anchor={[0.5, 0.9]}
      />
    ),
    [],
  );
  // Custom render function when clustering
  // This is for generaing cluster object in RLayerCluster
  const clusterRender = useCallback(
    (feature, resolution) => {
      // Heavy computation:
      // We gather routes of each endpoint then compute bounding
      const feats = feature.get("features");
      const size = feats.length;
      // We map to series of object w/properties `route`

      feature.on("click", (e) => {
        if (e.target.values_.features) {
          var features = e.target.values_.features;
          var ext = features[0].getGeometry().getExtent();
          for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            const rawRoutes = feature.values_.route;

            try {
              if (rawRoutes) {
                const routes = FeatureReader.readFeatures(dataToGeoJSON(longLatToGeo)(rawRoutes));
                const box = generateBoundingBox(routes);
                ext = extend(ext, box);
              }
            } catch (error) {
              console.log(error);
            }
          }
          e.map.getView().fit(ext);
        }
      });
      if (size > 1) {
        return (
          <>
            {BikeIcon}
            <RText text={size.toString()} font="80% sans-serif">
              <RFill color="#fff" />
              <RStroke color="rgba(0, 0, 0, 0.7)" width={4} />
            </RText>
          </>
        );
      }
      return BikeIcon;
    },
    [BikeIcon],
  );

  // Note: Most Layer here are dynamically generated with the flow:
  // - Convert API data to GeoJSON
  // - Parse GeoJSON into OpenLayer Features
  // - Map Features into RFeatures
  // - Put RFeatures in compatible RLayer
  // For the last item, RLayerVector works but not RLayerCluster.
  // A workaround is to detech and re-render the component.

  return (
    <RMap
      className="dashboard-heatmap"
      initial={defaultCenter}
      view={[view, setView]}
      onPreCompose={(e) => {
        // Grab reference to Map
        if (!thisMap.current) {
          thisMap.current = e.target;
        }
      }}
    >
      {/* Layer 1 - Base layer - OpenStreetMap - CyclOSM tile server */}
      <RLayerTile url="https://{a-c}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png" />
      {/* Layer 2 - Bike routes */}
      <RLayerVector zIndex={10}>
        <RStyle>
          <RStroke color="orange" width={5} />
          <RFill color="transparent" />
        </RStyle>
        {useMemo(
          () => (
            <>
              {routesJSON.map((f, i) => (
                <RFeature
                  key={`route-${i}`}
                  feature={f}
                  onClick={(e) => e.map.getView().fit(e.target.getGeometry().getExtent())}
                ></RFeature>
              ))}
            </>
          ),
          [routesJSON],
        )}
      </RLayerVector>
      {/* Layer 3 - Bike location clusters */}
      {isClusterOld ? (
        <></>
      ) : (
        <></>
        // <RLayerCluster zIndex={20} distance={30} features={bikeJSON}>
        //     <RStyle
        //         cacheSize={1024}
        //         cacheId={clusterCache}
        //         render={clusterRender}
        //     />
        // </RLayerCluster>
      )}
      {/* Add location layer */}
      <RLayerVector zIndex={20}>
        <RStyle>
          <RIcon
            src="https://inbooma.s3.eu-central-1.amazonaws.com/images/location_marker_icon_49.svg"
            anchor={[0.5, 0.8]}
          />
        </RStyle>
        {useMemo(
          () => (
            <>
              {locationJSON.map((f, i) => (
                <RFeature key={`location-${i}`} feature={f}></RFeature>
              ))}
            </>
          ),
          [locationJSON],
        )}
      </RLayerVector>
    </RMap>
  );
};
